import i18next from 'i18next';
import { useFetchRecruitisApi2 } from '@/js/utils/useFetchApi';
import { Ref, ref } from 'vue';

const DEFAULT_NAMESPACE: string = 'translation';
const LANG: string = document.documentElement?.lang ?? 'cs';

type I18nResources = { [key: string]: { [key: string]: { [key: string]: string } } };

const ciLastChange: string | undefined = document.querySelector('meta[name=lang_last_change]')?.getAttribute('content');
const cacheLastChange: string | undefined = window.localStorage.getItem('i18nLastUpdate');
const shouldInvalidateCache: boolean = ciLastChange !== cacheLastChange;

let resources: I18nResources = JSON.parse(window.localStorage.getItem('i18nMessages') ?? '{}');

if (shouldInvalidateCache) {
    window.localStorage.setItem('i18nLastUpdate', ciLastChange);
    resources = {};
}

export const getLang = () => i18next.options.lng ?? LANG;

const replaceVariableTemplates = (text: string) =>
    text.replace(/%(\d)/g, (match: string, contents): string => `{{${contents - 1}}}`);

const transformTranslations = (translations: { [key: string]: string }): { [key: string]: string } => {
    const transformedTranslations: { [key: string]: string } = {};

    for (const [key, translation] of Object.entries<string>(translations)) {
        transformedTranslations[key] = replaceVariableTemplates(translation);
    }

    return transformedTranslations;
};

const loadMessages = async (lang: string, libraries: string[]): Promise<void> => {
    try {
        const { data } = await useFetchRecruitisApi2(`/system/languages/${libraries.join('::')}/${lang}`)
            .get()
            .json();
        const langResources = resources[lang] ?? {};

        resources[lang] = {
            [DEFAULT_NAMESPACE]: {
                ...langResources[DEFAULT_NAMESPACE],
                ...transformTranslations(data.value?.payload ?? {}),
            },
        };
        i18next.addResources(lang, DEFAULT_NAMESPACE, resources[lang]);
        window.localStorage.setItem('i18nMessages', JSON.stringify(resources));
    } catch (e) {
        console.error(e);
    }
};

const runningFetches: { [key: string]: Promise<void> } = {};

const lazyLoadTranslation = async (key, variables): Promise<string> => {
    const library = key.split('.').shift();

    if (!runningFetches[library]) {
        runningFetches[library] = loadMessages(getLang(), [library]);
    }
    await runningFetches[library];
    return i18next.t(key, variables).toString();
};

const isTranslationLoaded = (key: string): boolean => {
    return (
        resources[getLang()] !== undefined &&
        resources[getLang()][DEFAULT_NAMESPACE] !== undefined &&
        resources[getLang()][DEFAULT_NAMESPACE][key] !== undefined
    );
};

const variableArrayToObject = (variables: Array<string | number>): { [key: string]: string | number } => {
    const variablesObject: { [key: string]: string | number } = {};

    for (const [index, variable] of variables.entries()) {
        variablesObject[index] = variable;
    }

    return variablesObject;
};

/**
 * Asynchronously translates a string using the provided key and variables.
 *
 * @param key The translation key
 * @param variables An array of variables to be used in the translation
 * @returns A Promise that resolves to the translated string
 */
export const translateAsync = async (key: string, ...variables: Array<string | number>): Promise<string> => {
    const variablesObject = variableArrayToObject(variables);

    if (isTranslationLoaded(key) === false) {
        return lazyLoadTranslation(key, variablesObject);
    }

    return i18next.t(key, variablesObject).toString();
};

/**
 * Translates a string and returns a reactive Ref<string>.
 *
 * @param key The translation key
 * @param variables An array of variables to be used in the translation
 * @returns A reactive Ref object that holds the translated string
 */
export const translate = (key: string, ...variables: Array<string | number>): Ref<string> => {
    const variablesObject = variableArrayToObject(variables);
    const result = ref(key);

    if (isTranslationLoaded(key) === false) {
        lazyLoadTranslation(key, variablesObject).then((translation) => {
            result.value = translation;
        });
    } else {
        result.value = i18next.t(key, variablesObject).toString();
    }

    return result;
};

const useI18n = async (language: string = LANG): Promise<{ install: (app) => void }> => {
    await i18next.init({
        lng: language,
        resources,
    });

    return {
        install: (app) => {
            app.config.globalProperties.$t = (key: string, ...variables: Array<string | number>) => {
                return translate(key, ...variables).value;
            };
        },
    };
};

export default useI18n;
